<template>
  <div :class="'ticket-reader ticket-reader-' + layout + (isScanning ? ' ticket-reader-scanning' : '')">
    <div class="ticket-reader-scanner" v-if="isScanning && !externalScanner" v-on:click="nextCamera">
      <video ref="preview" class="ticket-reader-video" playsinline muted autoplay></video>
      <div class="ticket-reader-aim" aria-hidden="true"></div>
    </div>
    <div class="ticket-reader-body">
      <template v-if="!isScanning">
        <div class="form-group" v-if="isEventSelectorVisible || hasSelectedEvents">
          <label>{{ _('Selected event(s)') }}</label>
          <select class="form-control" v-model="pendingEvent" v-on:change="addSelectedEvent" v-if="isEventSelectorVisible">
            <option :value="null">{{ _('Select an event') }}</option>
            <option v-for="event in events" :key="event.id" :value="event">{{ event.text }} {{ formatDate(event.start_date) }} ({{ event.resource_name }})</option>
          </select>
          <ul class="list-group">
            <li class="list-group-item ticket-reader-selected" v-for="se in selectedEvents" :key="se.id">
              <button type="button" class="btn btn-default ticket-reader-remove" v-on:click="removeSelectedEvent(se)">&times;</button>
              <span class="item-text">
                <span class="item-name">{{ se.text }}</span>
                <span class="item-times">
                  <span class="item-date">{{ formatDate(se.start_date) }}</span>
                  <span class="item-resource">{{ se.resource_name }}</span>
                </span>
              </span>
            </li>
          </ul>
        </div>
        <div class="form-group" v-if="isResourceSelectorVisible || hasSelectedResources">
          <label>{{ _('Selected resource(s)') }}</label>
          <select class="form-control" v-model="pendingResource" v-on:change="addSelectedResource" v-if="isResourceSelectorVisible">
            <option :value="null">{{ _('Select a resource') }}</option>
            <option v-for="resource in resources" :key="resource.id" :value="resource">{{ resource.name }}</option>
          </select>
          <ul class="list-group">
            <li class="list-group-item ticket-reader-selected" v-for="sr in selectedResources" :key="sr.id">
              <button type="button" class="btn btn-default ticket-reader-remove" v-on:click="removeSelectedResource(sr)">&times;</button>
              <span class="item-text"><span class="item-name">{{ sr.name }}</span></span>
            </li>
          </ul>
        </div>
        <div class="form-group" v-if="isTimeSelectorVisible || hasSelectedTimes">
          <label>{{ _('Selected start time(s)') }}</label>
          <select class="form-control" v-model="pendingTime" v-on:change="addSelectedTime" v-if="isTimeSelectorVisible">
            <option :value="null">{{ _('Select a time') }}</option>
            <option v-for="time in times" :key="time" :value="time">{{ formatTime(time) }}</option>
          </select>
          <ul class="list-group">
            <li class="list-group-item ticket-reader-selected" v-for="st in selectedTimes" :key="st">
              <button type="button" class="btn btn-default ticket-reader-remove" v-on:click="removeSelectedTime(st)">&times;</button>
              <span class="item-text"><span class="item-name">{{ formatTime(st) }} ({{ formatDay(st) }})</span></span>
            </li>
          </ul>
        </div>
        <div class="form-group">
          <label>{{ _('Use external scanner') }}</label>
          <select class="form-control" v-model="externalScannerSelect">
            <option :value="0">{{ _('No') }}</option>
            <option :value="1">{{ _('Yes') }}</option>
          </select>
        </div>
        <div v-if="!canStartScanning" class="alert alert-info">
          {{ _('Select events, or resources and start times, before scanning.') }}
        </div>
      </template>
      <template v-else>
        <div v-if="cameraError" class="alert alert-danger">{{ cameraError }}</div>
        <ul class="list-group" v-if="hasSelectedEvents">
          <li class="list-group-item ticket-reader-selected ticket-reader-scanning-item" v-for="se in selectedEvents" :key="se.id">
            <span class="item-text">
              <span class="item-name">{{ se.text }}</span>
              <span class="item-times">
                <span class="item-date">{{ formatDate(se.start_date) }}</span>
                <span class="item-resource">{{ se.resource_name }}<span v-if="showCapacity[se.id]"> ({{ showCapacity[se.id].capacity }})</span></span>
              </span>
            </span>
            <span class="item-capacity" v-if="showCapacity[se.id]">
              <span class="capacity-scanned">{{ showCapacity[se.id].confirmed }}</span>
              <span class="capacity-reserved">{{ showCapacity[se.id].reservations }}</span>
            </span>
          </li>
        </ul>
        <ul class="list-group" v-if="hasSelectedResources">
          <li class="list-group-item ticket-reader-selected" v-for="sr in selectedResources" :key="sr.id">
            <span class="item-text"><span class="item-name">{{ sr.name }}</span></span>
          </li>
        </ul>
        <div v-if="!ticket">
          <div v-if="scanStatus == 'invalid'" class="alert alert-danger">{{ _('The code is invalid.') }}</div>
          <div v-if="scanStatus == 'shop-mismatch'" class="alert alert-danger">{{ _('This code is for another Johku merchant.') }}</div>
          <div v-if="scanStatus == 'confirm-failed'" class="alert alert-danger">{{ _('Could not confirm the ticket. Please scan it again.') }}</div>
          <div v-if="scanStatus == 'reading'" class="alert alert-info">{{ _('Reading code...') }}</div>
          <div v-if="scanStatus == 'loading'" class="alert alert-info">{{ _('Loading ticket...') }}</div>
          <div v-if="!scanStatus" class="alert alert-info">{{ _('Please scan another code.') }}</div>
        </div>
      </template>
    </div>
    <div class="ticket-reader-footer">
      <button type="button" class="btn btn-success btn-block" v-if="!isScanning" :disabled="!canStartScanning" v-on:click="startScanning">{{ _('Start scanning') }}</button>
      <button type="button" class="btn btn-info btn-block" v-else v-on:click="showSettings">{{ _('Settings') }}</button>
    </div>

    <div class="ticket-reader-overlay alert alert-success" v-if="ticket && isValidForActiveTarget && ticket.ownSeat_ticket.length == 1 && parseInt(ticket.ownSeat_ticket[0].status, 10) === 0">
      <div class="scan-text-wrap">
        <div class="scan-text">
          <div class="reservation-info">
            <h4 class="reservation-show">{{ ticket.reservation.product_name }}</h4>
            <p class="reservation-details">
              <span class="reservation-time">{{ formatTimestamp(ticket.reservation.start_ts) }}</span>
              <span class="reservation-resource">{{ ticketResourceName }}</span>
            </p>
          </div>
          <h2 class="scan-ok text-center">
            <span v-for="seat in ticket.ownSeat_ticket" :key="seat.id">{{ getSeatDescription(seat.seat) }}</span>
          </h2>
          <div class="ticket-items" v-if="ticket.ticketitems">
            <div class="ticket-item" v-for="item in ticket.ticketitems" :key="item.id">{{ item.count }} x {{ item.product_name }}</div>
          </div>
        </div>
      </div>
      <div class="scan-buttons">
        <button type="button" class="btn btn-success btn-block" v-on:click="cancel">{{ _('Close') }}</button>
      </div>
    </div>

    <div class="ticket-reader-overlay alert alert-warning" v-if="ticket && isValidForActiveTarget && (ticket.personal || ticket.ownSeat_ticket.length > 1 || (ticket.ownSeat_ticket.length == 1 && parseInt(ticket.ownSeat_ticket[0].status, 10) !== 0))">
      <div class="scan-text-wrap">
        <div class="scan-text">
          <div class="reservation-info">
            <h4 class="reservation-show">{{ ticket.reservation.product_name }}</h4>
            <p class="reservation-details">
              <span class="reservation-time">{{ formatTimestamp(ticket.reservation.start_ts) }}</span>
              <span class="reservation-resource">{{ ticketResourceName }}</span>
            </p>
          </div>
          <div class="alert alert-info" v-if="ticket.personal">{{ _('This ticket is only usable by %s').replace('%s', ticket.personal) }}</div>
          <div class="ticket-items" v-if="ticket.ticketitems">
            <div class="ticket-item" v-for="item in ticket.ticketitems" :key="item.id">{{ item.count }} x {{ item.product_name }}</div>
          </div>
          <div class="confirm-buttons">
            <button type="button" :class="'btn btn-' + seatClass(seat)" v-on:click="toggleSeat(seat)" v-for="seat in ticket.ownSeat_ticket" :key="seat.id">
              {{ getSeatDescription(seat.seat) }}
            </button>
          </div>
        </div>
      </div>
      <div class="scan-buttons">
        <button type="button" class="btn btn-success btn-lg btn-block" :disabled="!selectedSeats.length" v-on:click="confirmSeats(false)">
          {{ selectedSeats.length ? _('Confirm selected') + ' (' + selectedSeats.length + ')' : _('Select seats') }}
        </button>
        <button type="button" class="btn btn-default btn-block" v-on:click="cancel">{{ _('Cancel') }}</button>
      </div>
    </div>

    <div class="ticket-reader-overlay alert alert-danger" v-if="ticket && !isValidForActiveTarget">
      <div class="scan-text-wrap">
        <div class="scan-text">
          <p><span v-if="selectedEvents && selectedEvents.length">{{ _('The ticket is not valid for the current show.') }}</span> {{ ticketDetails }}</p>
          <p v-if="ticket.reservation">
            {{ _('The scanned ticket is for') }}
            <strong>{{ ticket.reservation.product_name }} / {{ formatTimestamp(ticket.reservation.start_ts) }} ({{ ticketResourceName }})</strong>.
          </p>
        </div>
      </div>
      <div class="scan-buttons">
        <button type="button" class="btn btn-default btn-block" v-on:click="cancel">{{ _('Cancel') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { DateTime } from 'luxon'
import throttle from 'lodash/throttle'
import { mapGetters } from 'vuex'
import { parseTicketCode } from '~/lib/ticket-reader/parseTicketCode'
import { isEditableScanTarget } from '~/lib/scanWedge'

const STORAGE_PREFIX = 'johku-ticket-reader-'

export default {
  name: 'TicketReaderCore',
  props: {
    layout: {
      type: String,
      default: 'page'
    },
    listenWedge: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      events: [],
      resources: [],
      times: [],
      pendingEvent: null,
      pendingResource: null,
      pendingTime: null,
      selectedEvents: [],
      selectedResources: [],
      selectedTimes: [],
      externalScanner: false,
      ticket: null,
      ticketDetails: '',
      scanStatus: false,
      selectedSeats: [],
      showCapacity: {},
      cameraError: '',
      view: 'settings',
      lastAutomated: false,
      scanner: null,
      buffer: [],
      flushTimeout: null,
      keyListener: null
    }
  },
  computed: {
    ...mapGetters(['shopId', 'apiKey', 'baseDomain', 'dateFormat', 'timeFormat', 'timezone', 'locale']),
    isScanning () {
      return this.view === 'scan'
    },
    canStartScanning () {
      if (this.selectedEvents && this.selectedEvents.length) {
        return true
      }
      if (this.selectedResources && this.selectedResources.length && this.selectedTimes && this.selectedTimes.length) {
        return true
      }
      return false
    },
    hasSelectedTimes () {
      return this.selectedTimes && this.selectedTimes.length
    },
    isEventSelectorVisible () {
      return this.events && this.events.length && !(this.selectedResources && this.selectedResources.length)
    },
    hasSelectedEvents () {
      return this.selectedEvents && this.selectedEvents.length
    },
    isResourceSelectorVisible () {
      return this.resources && this.resources.length && !(this.selectedEvents && this.selectedEvents.length)
    },
    hasSelectedResources () {
      return this.selectedResources && this.selectedResources.length
    },
    isTimeSelectorVisible () {
      return this.times && this.times.length && this.isResourceSelectorVisible
    },
    externalScannerSelect: {
      get () {
        return this.externalScanner ? 1 : 0
      },
      set (value) {
        this.externalScanner = parseInt(value, 10) === 1
        this.persistState()
        this.syncCamera()
        this.syncWedge()
      }
    },
    isValidForActiveTarget () {
      if (!this.ticket || !this.ticket.ownSeat_ticket || !this.ticket.reservation) {
        return false
      }
      if (parseInt(this.ticket.reservation.status, 10) < 1) {
        this.ticketDetails = this._('The reservation has not been confirmed.')
        return false
      }
      if (this.selectedEvents && this.selectedEvents.length) {
        return this.isValidForActiveShow
      }
      return this.isValidForActiveResource
    },
    isValidForActiveShow () {
      if (!this.showIdMatches()) {
        this.ticketDetails = this._('The selected show id and the ticket\'s show id do not match.')
        return false
      }
      if (!this.resourceIdMatchesShow()) {
        this.ticketDetails = this._('The seats in this ticket are for a different resource.')
        return false
      }
      return true
    },
    isValidForActiveResource () {
      if (!this.resourceIdMatches()) {
        this.ticketDetails = this._('The seats in this ticket are for a different resource.')
        return false
      }
      if (!this.ticketTimeMatches()) {
        this.ticketDetails = this._('The ticket is not valid for the selected time.')
        return false
      }
      return true
    },
    ticketResourceName () {
      if (!this.ticket || !this.ticket.ownSeat_ticket || !this.ticket.reservation) {
        return ''
      }
      if (!this.selectedEvents || !this.selectedEvents.length) {
        for (let i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
          for (let j = 0; j < this.selectedResources.length; j++) {
            const sr = this.selectedResources[j]
            if (String(sr.id) === String(this.ticket.ownSeat_ticket[i].seat.resource_id)) {
              return sr.name
            }
          }
        }
        return ''
      }
      for (let i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
        for (let j = 0; j < this.selectedEvents.length; j++) {
          const se = this.selectedEvents[j]
          if (String(se.resource_id) === String(this.ticket.ownSeat_ticket[i].seat.resource_id)) {
            return se.resource_name
          }
        }
      }
      return ''
    }
  },
  watch: {
    canStartScanning () {
      this.syncCamera()
    },
    isScanning () {
      this.syncCamera()
      this.syncWedge()
    }
  },
  methods: {
    storageKey () {
      return STORAGE_PREFIX + this.shopId
    },
    persistState () {
      try {
        localStorage.setItem(this.storageKey(), JSON.stringify({
          selectedEvents: this.selectedEvents,
          selectedResources: this.selectedResources,
          selectedTimes: this.selectedTimes,
          externalScanner: this.externalScanner
        }))
      } catch (e) {}
    },
    restoreState () {
      try {
        const raw = localStorage.getItem(this.storageKey())
        if (!raw) {
          return
        }
        const data = JSON.parse(raw)
        this.selectedEvents = data.selectedEvents || []
        this.selectedResources = data.selectedResources || []
        this.selectedTimes = data.selectedTimes || []
        this.externalScanner = !!data.externalScanner
      } catch (e) {}
    },
    formatDate (date) {
      const format = (this.dateFormat || 'd.M.yyyy') + ' ' + (this.timeFormat || 'H.mm')
      const zone = this.timezone || 'local'
      let dt = DateTime.fromSQL(String(date), { zone })
      if (!dt.isValid) {
        dt = DateTime.fromISO(String(date).replace(' ', 'T'), { zone })
      }
      return dt.isValid ? dt.toFormat(format) : String(date)
    },
    formatTimestamp (ts) {
      const format = (this.dateFormat || 'd.M.yyyy') + ' ' + (this.timeFormat || 'H.mm')
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone || 'local' }).toFormat(format)
    },
    formatTime (ts) {
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone || 'local' }).toFormat(this.timeFormat || 'H.mm')
    },
    formatDay (ts) {
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone || 'local' }).toFormat(this.dateFormat || 'd.M.yyyy')
    },
    getSeatDescription (seat) {
      if (!seat) {
        return ''
      }
      if (seat.header || seat.title) {
        return (seat.header ? seat.header + ' / ' : '') + seat.title
      }
      return seat.name
    },
    showIdMatches () {
      return this.selectedEvents.some(se => String(se.id) === String(this.ticket.show_id))
    },
    resourceIdMatchesShow () {
      return this.ticket.ownSeat_ticket.some(seat => this.selectedEvents.some(se => String(se.resource_id) === String(seat.seat.resource_id)))
    },
    resourceIdMatches () {
      return this.ticket.ownSeat_ticket.some(seat => this.selectedResources.some(sr => String(sr.id) === String(seat.seat.resource_id)))
    },
    ticketTimeMatches () {
      return this.selectedTimes.some(ts => String(this.ticket.reservation.start_ts) === String(ts))
    },
    seatClass (seat) {
      if (parseInt(seat.status, 10) === 1) {
        return 'danger'
      }
      if (this.selectedSeats.indexOf(seat.seat_id) !== -1) {
        return 'primary'
      }
      return 'default'
    },
    addSelectedEvent () {
      if (!this.pendingEvent) {
        return
      }
      if (!this.selectedEvents.find(se => String(se.id) === String(this.pendingEvent.id))) {
        this.selectedEvents.push(this.pendingEvent)
      }
      this.pendingEvent = null
      this.persistState()
      this.updateSeatAllocations()
    },
    removeSelectedEvent (event) {
      this.selectedEvents = this.selectedEvents.filter(se => String(se.id) !== String(event.id))
      this.persistState()
      this.updateSeatAllocations()
    },
    addSelectedResource () {
      if (!this.pendingResource) {
        return
      }
      if (!this.selectedResources.find(sr => String(sr.id) === String(this.pendingResource.id))) {
        this.selectedResources.push(this.pendingResource)
      }
      this.pendingResource = null
      this.persistState()
      this.reloadTimes()
    },
    removeSelectedResource (resource) {
      this.selectedResources = this.selectedResources.filter(sr => String(sr.id) !== String(resource.id))
      this.persistState()
      this.reloadTimes()
    },
    addSelectedTime () {
      if (this.pendingTime == null) {
        return
      }
      if (this.selectedTimes.indexOf(this.pendingTime) === -1) {
        this.selectedTimes.push(this.pendingTime)
      }
      this.pendingTime = null
      this.persistState()
    },
    removeSelectedTime (time) {
      this.selectedTimes = this.selectedTimes.filter(st => st !== time)
      this.persistState()
    },
    startScanning () {
      if (!this.canStartScanning) {
        return
      }
      this.view = 'scan'
      this.cameraError = ''
      this.emitScanning(true)
      this.updateSeatAllocations()
    },
    showSettings () {
      this.cancel()
      this.view = 'settings'
      this.emitScanning(false)
    },
    emitScanning (active) {
      this.EventBus.$emit('ticketReader.scanning', active)
    },
    cancel () {
      this.ticket = null
      this.selectedSeats = []
      this.scanStatus = false
      this.ticketDetails = ''
    },
    toggleSeat (seat) {
      if (parseInt(seat.status, 10) !== 0) {
        return
      }
      const idx = this.selectedSeats.indexOf(seat.seat_id)
      if (idx !== -1) {
        this.selectedSeats.splice(idx, 1)
      } else {
        this.selectedSeats.push(seat.seat_id)
      }
    },
    selectSeats (identifier) {
      const stripped = identifier.substring(1)
      if (stripped === this.ticket.identifier) {
        for (let i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
          if (parseInt(this.ticket.ownSeat_ticket[i].status, 10) === 0) {
            this.selectedSeats.push(this.ticket.ownSeat_ticket[i].seat_id)
          }
        }
      } else {
        for (let i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
          if (this.ticket.ownSeat_ticket[i].identifier == identifier && parseInt(this.ticket.ownSeat_ticket[i].status, 10) === 0) {
            this.selectedSeats.push(this.ticket.ownSeat_ticket[i].seat_id)
          }
        }
      }
    },
    async processScan (content) {
      if (!this.isScanning || this.ticket) {
        return
      }
      this.selectedSeats = []
      this.scanStatus = false
      this.ticketDetails = ''
      const parsed = parseTicketCode(content)
      if (!parsed) {
        this.ticket = null
        this.scanStatus = 'invalid'
        setTimeout(() => { this.scanStatus = false }, 3000)
        return
      }
      if (parsed.shopId !== this.shopId) {
        this.ticket = null
        this.scanStatus = 'shop-mismatch'
        setTimeout(() => { this.scanStatus = false }, 3000)
        return
      }
      this.scanStatus = 'loading'
      try {
        const response = await axios.get(this.baseDomain + parsed.shopId + '/tickets/' + parsed.identifier + '.json')
        this.scanStatus = false
        this.ticket = response.data
        this.selectSeats(parsed.identifier)
        if (this.selectedSeats.length === 1 && this.ticket.ownSeat_ticket.length === 1 && this.isValidForActiveTarget && !this.ticket.personal) {
          this.confirmSeats(true)
        }
      } catch (e) {
        this.ticket = null
        this.scanStatus = 'invalid'
        setTimeout(() => { this.scanStatus = false }, 3000)
      }
    },
    async confirmSeats (automatedApproval) {
      if (typeof automatedApproval !== 'boolean') {
        automatedApproval = false
      }
      this.lastAutomated = automatedApproval
      if (!this.selectedSeats.length) {
        return
      }
      const ids = []
      for (let i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
        if (this.selectedSeats.indexOf(this.ticket.ownSeat_ticket[i].seat_id) !== -1) {
          ids.push(this.ticket.ownSeat_ticket[i].id)
        }
      }
      try {
        await Promise.all(ids.map(id => axios.patch(this.baseDomain + this.shopId + '/tickets/' + id + '.json', { id, status: 1 })))
        if (!this.lastAutomated) {
          this.ticket = null
          this.selectedSeats = []
        }
        this.updateSeatAllocations()
      } catch (e) {
        this.ticket = null
        this.selectedSeats = []
        this.scanStatus = 'confirm-failed'
        setTimeout(() => { this.scanStatus = false }, 4000)
      }
    },
    throttledUpdate: throttle(function () {
      this.updateSeatAllocations()
    }, 500, { leading: false, trailing: true }),
    async updateSeatAllocations () {
      const ids = this.selectedEvents.map(se => se.id)
      if (!ids.length) {
        this.showCapacity = {}
        return
      }
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/showschedule.json?ids=' + ids.join(','))
        if (response.data && response.data.data) {
          const capacity = {}
          response.data.data.forEach(s => {
            capacity[s.id] = {
              capacity: s.resource_capacity,
              reservations: s.resource_reservations,
              confirmed: s.resource_confirmed
            }
          })
          this.showCapacity = capacity
        }
      } catch (e) {}
    },
    async reloadTimes () {
      const resourceIds = this.selectedResources.map(r => r.id)
      if (!resourceIds.length) {
        this.times = []
        this.selectedTimes = []
        this.persistState()
        return
      }
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      const ts = d.getTime() / 1000
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/tickets.json?starttimes=true&ts=' + ts + '&resources=' + resourceIds.join(','))
        this.times = response.data || []
      } catch (e) {
        this.times = []
      }
    },
    cameraErrorMessage (error) {
      const name = error && error.name
      if (name === 'NotAllowedError') {
        return this._('Camera permission is blocked. Allow camera access for this site in the browser settings.')
      }
      if (name === 'NotFoundError') {
        return this._('No camera was found on this device.')
      }
      return this._('Could not start the camera.')
    },
    async startCamera () {
      this.stopCamera()
      if (this.externalScanner || !this.isScanning) {
        return
      }
      await this.$nextTick()
      const video = this.$refs.preview
      if (!video) {
        return
      }
      this.cameraError = ''
      try {
        const { CameraQrScanner } = await import('~/lib/ticket-reader/cameraScanner')
        this.scanner = new CameraQrScanner({
          video,
          onScan: (content) => this.processScan(content),
          onError: (error) => { this.cameraError = this.cameraErrorMessage(error) }
        })
        await this.scanner.start()
      } catch (error) {
        this.cameraError = this.cameraErrorMessage(error)
        this.stopCamera()
      }
    },
    stopCamera () {
      if (this.scanner) {
        this.scanner.stop()
        this.scanner = null
      }
    },
    syncCamera () {
      if (this.externalScanner || !this.isScanning) {
        this.stopCamera()
        return
      }
      this.startCamera()
    },
    syncWedge () {
      if (!this.listenWedge) {
        return
      }
      const shouldListen = this.isScanning && this.externalScanner
      if (shouldListen && !this.keyListener) {
        this.keyListener = (event) => this.onKeypress(event)
        window.addEventListener('keypress', this.keyListener)
      } else if (!shouldListen && this.keyListener) {
        window.removeEventListener('keypress', this.keyListener)
        this.keyListener = null
      }
    },
    nextCamera () {
      if (this.scanner) {
        this.scanner.nextCamera()
      }
    },
    flushBuffer () {
      const code = this.buffer.join('')
      this.buffer = []
      if (code) {
        this.processScan(code)
      }
    },
    onKeypress (event) {
      clearTimeout(this.flushTimeout)
      if (isEditableScanTarget(event)) {
        return
      }
      if (event && event.key && event.key.match(/^[0-9a-z:/\-_.]$/i)) {
        this.scanStatus = 'reading'
        this.buffer.push(event.key)
        this.flushTimeout = setTimeout(() => this.flushBuffer(), 500)
      }
      if (event && (event.keyCode === 13 || event.key === 'Enter')) {
        clearTimeout(this.flushTimeout)
        if (this.buffer.length) {
          this.flushBuffer()
        }
      }
    },
    onAvailabilityUpdate (message) {
      if (!message || !message.data || !this.selectedEvents.length) {
        return
      }
      const products = message.data.affectedProducts || []
      const resources = message.data.affectedResources || []
      const match = this.selectedEvents.some(se =>
        products.some(item => String(item) === String(se.product_id)) ||
        resources.some(item => String(item) === String(se.resource_id))
      )
      if (match) {
        this.throttledUpdate()
      }
    },
    async loadCatalog () {
      const d = new Date()
      const dateStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
      try {
        const shows = await axios.get(this.baseDomain + this.shopId + '/showschedule.json?from=' + dateStr)
        this.events = (shows.data && shows.data.data) || []
      } catch (e) {
        this.events = []
      }
      try {
        const resources = await axios.get(this.baseDomain + this.shopId + '/resources.json?withtickets=true')
        this.resources = resources.data || []
      } catch (e) {
        this.resources = []
      }
      if (this.selectedResources.length) {
        this.reloadTimes()
      }
      this.updateSeatAllocations()
    }
  },
  mounted () {
    this.restoreState()
    this.loadCatalog()
    this.EventBus.$on('ticketReader.processScan', this.processScan)
    this.EventBus.$on('availabilityUpdate', this.onAvailabilityUpdate)
    this.emitScanning(false)
    this.syncWedge()
    this.syncCamera()
  },
  beforeUnmount () {
    this.stopCamera()
    this.emitScanning(false)
    this.EventBus.$off('ticketReader.processScan', this.processScan)
    this.EventBus.$off('availabilityUpdate', this.onAvailabilityUpdate)
    if (this.keyListener) {
      window.removeEventListener('keypress', this.keyListener)
      this.keyListener = null
    }
    if (this.throttledUpdate && this.throttledUpdate.cancel) {
      this.throttledUpdate.cancel()
    }
  }
}
</script>

<style scoped>
.ticket-reader {
  position: relative;
  min-height: 240px;
  display: flex;
  flex-direction: column;
}
.ticket-reader-page {
  min-height: calc(100dvh - 52px);
}
.ticket-reader-scanner {
  position: relative;
  background-color: #bec9d0;
  border-bottom: 3px solid rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 200px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
}
.ticket-reader-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
}
.ticket-reader-aim {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 50%;
  height: 80%;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.28);
  pointer-events: none;
}
.ticket-reader-body {
  padding: 15px;
  flex: 1;
}
.ticket-reader-footer {
  padding: 0 15px 15px;
  flex-shrink: 0;
}
.ticket-reader-selected {
  position: relative;
  padding-right: 45px;
}
.ticket-reader-scanning-item {
  padding-right: 45px;
}
.ticket-reader-selected .item-name {
  font-weight: bold;
  display: block;
  margin-bottom: 4px;
}
.ticket-reader-selected .item-times {
  font-size: 12px;
  display: block;
}
.ticket-reader-selected .item-date {
  padding-right: 15px;
}
.ticket-reader-selected .item-capacity {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  border-left: 1px solid #dddddd;
  text-align: center;
}
.ticket-reader-selected .item-capacity span {
  display: block;
}
.ticket-reader-selected .capacity-scanned {
  font-weight: bold;
  margin-top: 7px;
  margin-bottom: 1px;
}
.ticket-reader-remove {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  background: transparent;
  border: 0;
  border-left: 1px solid #dddddd;
  width: 40px;
  padding: 0;
}
.ticket-reader-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}
.ticket-reader-page .ticket-reader-overlay {
  position: fixed;
  top: 52px;
}
.scan-text-wrap {
  flex-grow: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
}
.scan-buttons {
  padding: 10px 15px 15px;
}
.scan-ok {
  border-bottom: none;
  color: #0C300D;
}
.ticket-items {
  font-weight: bold;
  margin-bottom: 15px;
}
.confirm-buttons .btn {
  margin: 4px;
}
</style>
